import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  createSideConversation,
  createIncidentFromSideConversation,
  handleSideConversationSuccess,
  popupError,
  popupSuccess,
  getCategoriaValueFromCurrentTicket,
  type NewOrderFormData as ZendeskNewOrderFormData,
} from "../../services/Zendesk";
import { useZendeskData } from "../../hooks";
import type { SelectedProduct, ProductResult, UserResult } from "./interfaces";

const newOrderSchema = z.object({
  solicitante: z.string().min(1, "Solicitante é obrigatório"),
  valorFrete: z.string().optional(),
  canalVenda: z.string().optional(),
  tipoDoTicket: z.string().min(1, "Tipo do ticket é obrigatório"),
  categoriaTicket: z.string().optional(),

  estabelecimento: z.string().min(1, "Estabelecimento é obrigatório"),
  tipoEnvio: z.string().min(1, "Tipo de envio é obrigatório"),
  pedidoInterno: z.string().min(1, "Pedido interno é obrigatório"),
  clonarPedido: z.boolean().optional(),
  classificacaoTroca: z.string().optional(),
  avaliacaoCredito: z.string().optional(),
  plataforma: z.string().optional(),
  retira: z.string().optional(),
  numeroParcelas: z.number().optional(),
  ecomPj: z.boolean().optional(),
  observacoes: z.string().optional(),
  evidenciaMotivo: z.string().optional(),
  statusIntegracao: z.string().min(1, "Status da integração é obrigatório"),
  tipoRegistro: z.string().min(1, "Tipo do registro é obrigatório"),
  dataIntegracao: z.string().optional(),
});

type NewOrderFormData = z.infer<typeof newOrderSchema>;

// Interface para armazenar informações dos pedidos criados
interface CreatedOrderInfo {
  sideConversationId: string;
  pedidoId: string;
  incidentId: number;
  childTicketId?: number; // ID do ticket problem (side conversation)
}

export function useNewOrder() {
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [savedOrderData, setSavedOrderData] = useState<NewOrderFormData | null>(
    null,
  );
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );
  const [selectedSolicitante, setSelectedSolicitante] =
    useState<UserResult | null>(null);
  const [createdOrders, setCreatedOrders] = useState<CreatedOrderInfo[]>([]);

  // Debug: Log quando selectedProducts muda
  useEffect(() => {
    console.log("🔧 DEBUG: selectedProducts atualizado:", selectedProducts);
  }, [selectedProducts]);

  // Debug: Log quando selectedSolicitante muda
  useEffect(() => {
    console.log(
      "🔧 DEBUG: selectedSolicitante atualizado:",
      selectedSolicitante,
    );
  }, [selectedSolicitante]);

  const { ticketId, currentUser, requester, brand } = useZendeskData();

  // Opções do tipo do ticket baseadas na marca
  const tipoTicketOptions = useMemo(
    () => [
      { value: "SAC AT", label: "SAC AT" },
      { value: "SAC ECOM", label: "SAC ECOM" },
      { value: "Jurídico", label: "Jurídico" },
    ],
    [],
  );

  const getInitialTipoDoTicket = useCallback(() => {
    const brandName = brand?.name || "";

    switch (brandName.toUpperCase()) {
      case "SAC ECOM":
      case "ECOM":
        return "SAC ECOM";
      case "JURÍDICO":
      case "JURIDICO":
      case "Jurídico":
        return "Jurídico";
      default:
        return "SAC AT";
    }
  }, [brand]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
    watch,
  } = useForm<NewOrderFormData>({
    resolver: zodResolver(newOrderSchema),
    defaultValues: {
      solicitante: "",
      valorFrete: "",
      canalVenda: "",
      tipoDoTicket: getInitialTipoDoTicket(),
      categoriaTicket: "",
      estabelecimento: "",
      tipoEnvio: "",
      pedidoInterno: "",
      clonarPedido: false,
      classificacaoTroca: "",
      avaliacaoCredito: "",
      plataforma: "",
      retira: "",
      numeroParcelas: 0,
      ecomPj: false,
      observacoes: "",
      evidenciaMotivo: "",
      statusIntegracao: "pedido_status_da_integracao_pendente",
      tipoRegistro: "pedido_de_troca",
      dataIntegracao: "",
    },
  });

  // Atualizar tipoDoTicket quando os dados do brand chegarem
  useEffect(() => {
    if (brand) {
      const initialTipoDoTicket = getInitialTipoDoTicket();
      setValue("tipoDoTicket", initialTipoDoTicket);
    }
  }, [brand, setValue, getInitialTipoDoTicket]);

  // Buscar e definir categoriaTicket baseado na marca
  useEffect(() => {
    const loadCategoriaFromTicket = async () => {
      if (brand?.name) {
        console.log(
          "🔧 DEBUG: Buscando categoria do ticket para brand:",
          brand.name,
        );

        try {
          const categoriaValue = await getCategoriaValueFromCurrentTicket(
            brand.name,
          );

          if (categoriaValue) {
            console.log(
              "🔧 DEBUG: Categoria obtida do ticket:",
              categoriaValue,
            );
            setValue("categoriaTicket", categoriaValue);
          } else {
            console.log(
              "🔧 DEBUG: Nenhuma categoria encontrada no ticket, mantendo vazio",
            );
            setValue("categoriaTicket", "");
          }
        } catch (error) {
          console.error("❌ Erro ao buscar categoria do ticket:", error);
          setValue("categoriaTicket", "");
        }
      }
    };

    loadCategoriaFromTicket();
  }, [brand, setValue]);

  // Definir automaticamente o solicitante baseado no requester do ticket
  useEffect(() => {
    if (requester && !selectedSolicitante) {
      console.log(
        "🔧 DEBUG: Definindo solicitante automático baseado no requester:",
        requester,
      );

      // Criar objeto UserResult baseado nos dados do requester
      const requesterAsUser: UserResult = {
        kind: "user",
        id: String(requester.id),
        name: requester.name,
        email: requester.email,
        role: undefined, // O requester pode não ter role definida
        organization: undefined,
      };

      setSelectedSolicitante(requesterAsUser);
      setValue("solicitante", requester.name);

      console.log(
        "✅ DEBUG: Solicitante definido automaticamente:",
        requesterAsUser,
      );
      console.log(
        "🔧 DEBUG: Campo solicitante preenchido com:",
        requester.name,
      );
    }
  }, [requester, selectedSolicitante, setValue]);

  // Step 1: Salvar dados do pedido
  const onSubmitStep1 = (data: NewOrderFormData) => {
    if (!selectedSolicitante) {
      popupError("Selecione um solicitante antes de prosseguir", false);
      return;
    }
    console.log("🔧 DEBUG: Dados do step 1:", data);
    console.log("🔧 DEBUG: data.solicitante:", data.solicitante);
    console.log("🔧 DEBUG: selectedSolicitante atual:", selectedSolicitante);

    // Garantir que o campo solicitante seja preenchido com o nome do usuário selecionado
    const updatedData = {
      ...data,
      solicitante: selectedSolicitante.name,
    };

    console.log("🔧 DEBUG: Dados atualizados com solicitante:", updatedData);
    setSavedOrderData(updatedData);
    setStep(2);
  };

  // Debug: monitorar mudanças no selectedProducts
  useEffect(() => {
    console.log("🔧 DEBUG: selectedProducts state changed:", selectedProducts);
    console.log("🔧 DEBUG: selectedProducts length:", selectedProducts.length);
  }, [selectedProducts]);

  const addProduct = (
    product: ProductResult,
    quantidade: number,
    valorUnitario: string,
  ) => {
    console.log("🔧 DEBUG: addProduct chamado com:", {
      product,
      quantidade,
      valorUnitario,
    });
    console.log("🔧 DEBUG: selectedProducts atual:", selectedProducts);
    console.log("🔧 DEBUG: product.id:", product.id);
    console.log("🔧 DEBUG: product.name:", product.name);

    const existingProduct = selectedProducts.find((p) => p.id === product.id);
    console.log("🔧 DEBUG: existingProduct encontrado:", existingProduct);

    if (existingProduct) {
      console.log("⚠️ DEBUG: Produto já existe na lista, ignorando");
      return;
    }

    const newProduct: SelectedProduct = {
      id: product.id,
      name: product.name,
      quantidade,
      valorUnitario,
      custom_object_fields: product.custom_object_fields,
    };

    console.log("✅ DEBUG: Adicionando novo produto:", newProduct);
    console.log("🔧 DEBUG: Estado anterior dos produtos:", selectedProducts);

    setSelectedProducts((prev) => {
      const newState = [...prev, newProduct];
      console.log(
        "🔧 DEBUG: Novo estado dos produtos dentro do setter:",
        newState,
      );
      console.log("🔧 DEBUG: Prev state:", prev);
      return newState;
    });

    console.log("🔧 DEBUG: setSelectedProducts executado");
  };

  const removeProduct = (productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.id !== productId));
  };

  const updateProduct = (
    productId: string,
    updates: Partial<SelectedProduct>,
  ) => {
    console.log("🔧 DEBUG: updateProduct chamado com:", { productId, updates });
    setSelectedProducts((prev) => {
      const updated = prev.map((p) =>
        p.id === productId ? { ...p, ...updates } : p,
      );
      console.log("✅ DEBUG: Produtos após update:", updated);
      return updated;
    });
  };

  const onFinalSubmit = async () => {
    if (!savedOrderData) {
      popupError("Dados do pedido não encontrados", false);
      return;
    }

    console.log(
      "🔧 DEBUG: savedOrderData.solicitante:",
      savedOrderData.solicitante,
    );
    console.log("🔧 DEBUG: selectedSolicitante:", selectedSolicitante);
    console.log("🔧 DEBUG: clonarPedido:", savedOrderData.clonarPedido);

    if (!ticketId || !currentUser || !requester) {
      console.error("Dados do Zendesk não encontrados");
      popupError("Dados do Zendesk não encontrados", false);
      return;
    }

    if (selectedProducts.length === 0) {
      popupError("Adicione pelo menos um produto", false);
      return;
    }

    if (!selectedSolicitante) {
      popupError("Selecione um solicitante", false);
      return;
    }

    // Usar o ID do solicitante selecionado ou fallback para o requester padrão
    const requesterId =
      selectedSolicitante && selectedSolicitante.id
        ? parseInt(selectedSolicitante.id)
        : requester.id;

    console.log(
      "🔧 DEBUG: Usando requesterId:",
      requesterId,
      "- Solicitante:",
      selectedSolicitante?.name || "padrão",
    );

    setIsLoading(true);

    try {
      // Determinar quantas vezes criar (1 ou 2 baseado no checkbox clonarPedido)
      const quantidadePedidos = savedOrderData.clonarPedido ? 2 : 1;
      console.log(
        `🔧 DEBUG: Criando ${quantidadePedidos} pedido(s)${savedOrderData.clonarPedido ? " (clone ativado)" : ""}`,
      );

      const createdPedidos: CreatedOrderInfo[] = [];

      // Criar os pedidos (1 ou 2)
      for (let i = 0; i < quantidadePedidos; i++) {
        const pedidoNumber = i + 1;
        console.log(
          `🔧 DEBUG: Criando pedido ${pedidoNumber}/${quantidadePedidos}`,
        );

        const sideConversationResponse = await createSideConversation(
          ticketId,
          {
            ...savedOrderData,
            categoriaTicket: savedOrderData.categoriaTicket || "",
            dataIntegracao:
              savedOrderData.dataIntegracao ||
              new Date().toISOString().split("T")[0],
          } as ZendeskNewOrderFormData,
          selectedProducts,
          {
            requesterId, // Passar o ID do solicitante selecionado
            solicitanteNome: selectedSolicitante?.name, // Passar o nome do solicitante selecionado
          },
        );

        const sideConversationId =
          sideConversationResponse.side_conversation.id;

        // Extrair o pedido ID gerado da side conversation para usar nos incidents
        const sideConvSubject =
          sideConversationResponse.side_conversation.subject || "";
        const pedidoIdMatch = sideConvSubject.match(/Novo Pedido - (.+)/);
        const generatedPedidoId = pedidoIdMatch
          ? pedidoIdMatch[1]
          : savedOrderData.pedidoInterno;

        // Atualizar o orderData com o pedido ID gerado
        const updatedOrderData = {
          ...savedOrderData,
          pedidoInterno: generatedPedidoId,
        };

        console.log(
          `🔧 DEBUG: Pedido ${pedidoNumber} - Usando pedido ID: ${generatedPedidoId} para criar incidents`,
        );

        const incidentResponse = await createIncidentFromSideConversation(
          sideConversationId,
          {
            ...updatedOrderData,
            categoriaTicket: updatedOrderData.categoriaTicket || "",
            dataIntegracao:
              updatedOrderData.dataIntegracao ||
              new Date().toISOString().split("T")[0],
          } as ZendeskNewOrderFormData,
          selectedProducts,
          ticketId,
          currentUser.id,
          requesterId, // Usar o ID do solicitante selecionado
        );

        const incidentId = incidentResponse.ticket.id;
        const childTicketId = incidentResponse.childTicketId;

        // Armazenar informações do pedido criado
        createdPedidos.push({
          sideConversationId,
          pedidoId: generatedPedidoId,
          incidentId,
          childTicketId, // Incluir o childTicketId
        });

        console.log(
          `✅ DEBUG: Pedido ${pedidoNumber} criado - Side Conversation: ${sideConversationId}, Incident: #${incidentId}`,
        );

        // Chamar handleSideConversationSuccess para cada side conversation
        await handleSideConversationSuccess(
          sideConversationId,
          ticketId,
          generatedPedidoId,
        );
      }

      // Criar mensagem de sucesso baseada na quantidade de pedidos criados
      let message = "";
      if (quantidadePedidos === 1) {
        const productCount = selectedProducts.length;
        message =
          productCount > 1
            ? `${productCount} Incidents criados com sucesso! (Principal: #${createdPedidos[0].incidentId})`
            : `Incident #${createdPedidos[0].incidentId} criado com sucesso!`;
      } else {
        // Modo clone - 2 pedidos criados
        const totalIncidents = selectedProducts.length * 2;
        message = `${totalIncidents} Incidents criados com sucesso! (Clone ativado - 2 pedidos criados)`;
        console.log(`🔧 DEBUG: Pedidos clonados criados:`, createdPedidos);
      }

      // Salvar informações dos pedidos criados
      setCreatedOrders(createdPedidos);

      popupSuccess(message, true);
      setSuccess(true);

      // Abrir o último ticket problem criado após 5 segundos (na tela de sucesso)
      if (createdPedidos.length > 0) {
        const ultimoPedido = createdPedidos[createdPedidos.length - 1];
        if (ultimoPedido.childTicketId) {
          setTimeout(async () => {
            try {
              // Importar zafClient do arquivo zendesk.ts
              const { zafClient } =
                await import("../../services/Zendesk/zendesk");

              console.log(
                `🔗 Abrindo ticket problem (side conversation) #${ultimoPedido.childTicketId} após 5 segundos...`,
              );
              await zafClient.invoke(
                "routeTo",
                "ticket",
                ultimoPedido.childTicketId,
              );
              console.log(
                `✅ Ticket problem #${ultimoPedido.childTicketId} aberto com sucesso!`,
              );
            } catch (error) {
              console.warn(
                "⚠️ Erro ao abrir ticket problem, mas criação foi bem-sucedida:",
                error,
              );
              // Não falhar o processo se não conseguir abrir o ticket
            }
          }, 5000); // 5 segundos de delay
        }
      }
    } catch (error) {
      console.error("Erro completo:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Erro desconhecido";
      popupError(`Erro ao criar pedido: ${errorMessage}`, false);
    } finally {
      setIsLoading(false);
    }
  };

  // Observar o valor de tipoDoTicket para validação condicional
  const tipoDoTicket = watch("tipoDoTicket");

  return {
    navigate,
    step,
    setStep,
    success,
    setSuccess,
    isLoading,
    ticketId,
    savedOrderData,
    selectedProducts,
    selectedSolicitante,
    setSelectedSolicitante,
    tipoTicketOptions,
    createdOrders,
    register,
    handleSubmit,
    errors,
    onSubmit: onSubmitStep1,
    handleFinalSubmit: onFinalSubmit,
    addProduct,
    removeProduct,
    updateProduct,
    setValue,
    control,
    watch,
    tipoDoTicket,
  };
}
